/**
 * Member Workouts Page - Olympic Fit Wellness
 * Track and log workouts
 */
import { useState, useEffect } from 'react';
import { 
  Dumbbell, 
  Clock,
  Flame,
  TrendingUp,
  Plus,
  ChevronRight,
  Calendar,
  Play,
  X,
  Loader2,
  Check
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function WorkoutsPage() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('log');
  const [workouts, setWorkouts] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState({
    workouts: 0,
    totalMinutes: 0,
    totalCalories: 0,
    streak: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  const [workoutForm, setWorkoutForm] = useState({
    name: '',
    type: 'strength',
    duration: 30,
    calories: 0,
    exercises: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [workoutsData, statsData] = await Promise.all([
        api.getMyWorkouts(20),
        api.getWorkoutStats()
      ]);
      setWorkouts(workoutsData || []);
      setWeeklyStats(statsData || { workouts: 0, totalMinutes: 0, totalCalories: 0, streak: 0 });
    } catch (err) {
      console.error('Error loading workouts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogWorkout = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      await api.logWorkout(workoutForm);
      setSuccess('Workout logged successfully!');
      setShowLogModal(false);
      setWorkoutForm({
        name: '',
        type: 'strength',
        duration: 30,
        calories: 0,
        exercises: 0,
        notes: ''
      });
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to log workout');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDemandWorkouts = [
    {
      id: 'od_001',
      name: '20-Min HIIT Burn',
      instructor: 'Mike Johnson',
      duration: 20,
      difficulty: 'Intermediate',
      thumbnail: 'https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=400'
    },
    {
      id: 'od_002',
      name: 'Full Body Strength',
      instructor: 'Jessica Park',
      duration: 45,
      difficulty: 'Intermediate',
      thumbnail: 'https://images.unsplash.com/photo-1581009146145-b5ef050c149a?w=400'
    },
    {
      id: 'od_003',
      name: 'Relaxing Yoga Flow',
      instructor: 'Sarah Chen',
      duration: 30,
      difficulty: 'All Levels',
      thumbnail: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'
    },
    {
      id: 'od_004',
      name: 'Core Crusher',
      instructor: 'David Lee',
      duration: 15,
      difficulty: 'Advanced',
      thumbnail: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400'
    }
  ];

  const getTypeColor = (type) => {
    switch(type) {
      case 'strength': return 'bg-purple-100 text-purple-600';
      case 'cardio': return 'bg-orange-100 text-orange-600';
      case 'flexibility': return 'bg-green-100 text-green-600';
      case 'hiit': return 'bg-red-100 text-red-600';
      default: return 'bg-blue-100 text-blue-600';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="workouts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-neutral-900">My Workouts</h1>
          <p className="text-neutral-500">Track your progress and stay motivated</p>
        </div>
        <button
          onClick={() => setShowLogModal(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors"
          data-testid="log-workout-btn"
        >
          <Plus className="w-4 h-4" />
          LOG WORKOUT
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <X className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Weekly Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Workouts', value: weeklyStats.workouts, icon: Dumbbell, color: 'bg-purple-100 text-purple-600' },
          { label: 'Minutes', value: weeklyStats.totalMinutes, icon: Clock, color: 'bg-blue-100 text-blue-600' },
          { label: 'Calories', value: weeklyStats.totalCalories.toLocaleString(), icon: Flame, color: 'bg-orange-100 text-orange-600' },
          { label: 'Day Streak', value: weeklyStats.streak, icon: TrendingUp, color: 'bg-green-100 text-green-600' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm p-4">
            <div className={`w-10 h-10 rounded-lg ${stat.color.split(' ')[0]} flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.color.split(' ')[1]}`} />
            </div>
            <div className="text-2xl font-bold text-neutral-900">{stat.value}</div>
            <div className="text-sm text-neutral-500">{stat.label} this week</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-neutral-200">
        {[
          { id: 'log', name: 'Workout Log' },
          { id: 'ondemand', name: 'On-Demand' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-4 px-2 font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-[#3B82F6] border-b-2 border-[#3B82F6]'
                : 'text-neutral-500 hover:text-neutral-900'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {activeTab === 'log' && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-xl font-heading font-bold text-neutral-900">Recent Workouts</h2>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
            </div>
          ) : workouts.length > 0 ? (
            <div className="divide-y divide-neutral-100">
              {workouts.map((workout) => (
                <div key={workout.id} className="p-4 flex items-center gap-4 hover:bg-neutral-50 transition-colors">
                  <div className={`w-12 h-12 rounded-xl ${getTypeColor(workout.type).split(' ')[0]} flex items-center justify-center`}>
                    <Dumbbell className={`w-6 h-6 ${getTypeColor(workout.type).split(' ')[1]}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-neutral-900">{workout.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(workout.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {workout.duration} min
                      </span>
                      {workout.calories > 0 && (
                        <span className="flex items-center gap-1">
                          <Flame className="w-4 h-4" />
                          {workout.calories} cal
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(workout.type)}`}>
                    {workout.type}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Dumbbell className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
              <h3 className="font-semibold text-neutral-900 mb-2">No workouts logged yet</h3>
              <p className="text-neutral-500 mb-4">Start tracking your fitness journey!</p>
              <button
                onClick={() => setShowLogModal(true)}
                className="inline-flex items-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Log Your First Workout
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'ondemand' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {onDemandWorkouts.map((workout) => (
            <div key={workout.id} className="bg-white rounded-xl shadow-sm overflow-hidden group cursor-pointer">
              <div className="relative aspect-video">
                <img
                  src={workout.thumbnail}
                  alt={workout.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-6 h-6 text-[#3B82F6] ml-1" />
                  </div>
                </div>
                <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {workout.duration} min
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-neutral-900 mb-1">{workout.name}</h3>
                <p className="text-sm text-neutral-500">{workout.instructor}</p>
                <span className="inline-block mt-2 text-xs font-medium text-[#3B82F6]">
                  {workout.difficulty}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Log Workout Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold">Log Workout</h2>
              <button onClick={() => setShowLogModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleLogWorkout} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Workout Name</label>
                <input
                  type="text"
                  value={workoutForm.name}
                  onChange={(e) => setWorkoutForm({...workoutForm, name: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  placeholder="e.g., Morning HIIT Session"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                <select
                  value={workoutForm.type}
                  onChange={(e) => setWorkoutForm({...workoutForm, type: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                >
                  <option value="strength">Strength</option>
                  <option value="cardio">Cardio</option>
                  <option value="hiit">HIIT</option>
                  <option value="flexibility">Flexibility/Yoga</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Duration (min)</label>
                  <input
                    type="number"
                    value={workoutForm.duration}
                    onChange={(e) => setWorkoutForm({...workoutForm, duration: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Calories Burned</label>
                  <input
                    type="number"
                    value={workoutForm.calories}
                    onChange={(e) => setWorkoutForm({...workoutForm, calories: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                    min="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Exercises Count</label>
                <input
                  type="number"
                  value={workoutForm.exercises}
                  onChange={(e) => setWorkoutForm({...workoutForm, exercises: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
                <textarea
                  value={workoutForm.notes}
                  onChange={(e) => setWorkoutForm({...workoutForm, notes: e.target.value})}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6] resize-none"
                  rows={3}
                  placeholder="How did it go?"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="flex-1 py-3 border border-neutral-200 rounded-lg font-semibold text-sm hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-[#3B82F6] text-white rounded-lg font-semibold text-sm hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Saving...' : 'Log Workout'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
